// Thin wrapper around `focus-trap-react`'s named `FocusTrap` export so
// Modal / Drawer can render a single primitive instead of importing the
// library at every call-site (and so tests can stub it via
// `vi.mock('./shared/ui/_FocusTrap')` — the existing Modal / Drawer
// tests do exactly this).
//
// History note: an earlier revision of this file did a dynamic import
// guarded by `@vite-ignore` so the app would still boot if
// `focus-trap-react` wasn't installed. That escape hatch was over-
// engineered — npm install is a hard prereq for everything else in
// the app — and it had a race that left the wrapped component in a
// bad shape under Vite dev, surfacing as "Cannot destructure property
// 'children' of 'undefined'" the first time a user opened the cart
// drawer or booking modal. Static import resolved the whole problem;
// the test mocks above keep it inert in jsdom.

import type { ReactNode } from 'react';
import { FocusTrap } from 'focus-trap-react';

interface InnerProps {
  active: boolean;
  children: ReactNode;
  // focus-trap rejects `null` returns; callers fall back to
  // `document.body` to satisfy the non-null contract.
  fallbackFocus: () => HTMLElement;
}

export const FocusTrapBoundary = ({ active, children, fallbackFocus }: InnerProps) => {
  return (
    <FocusTrap
      active={active}
      focusTrapOptions={{
        escapeDeactivates: false,
        allowOutsideClick: true,
        fallbackFocus,
        returnFocusOnDeactivate: true,
      }}
    >
      {children}
    </FocusTrap>
  );
};
